// Fits the camera's near/far depth range to the content over the whole timeline.

import * as THREE from 'three';
import { buildObjects, pose } from './render';
import type { SceneDesc } from './types';

export function fitDepthRange(scene: SceneDesc, samples = 24): { near: number; far: number } | null {
  const material = new THREE.MeshBasicMaterial();
  const { root, objects } = buildObjects(scene, material);
  const s = new THREE.Scene();
  s.add(root);
  let lo = Infinity;
  let hi = -Infinity;
  const v = new THREE.Vector3();
  const n = scene.timeline.duration > 0 ? samples : 1;
  for (let i = 0; i < n; i++) {
    const c = pose(scene, objects, (i / n) * scene.timeline.duration);
    s.updateMatrixWorld(true);
    const eye = new THREE.Vector3(...c.position);
    const fwd = new THREE.Vector3(...c.target).sub(eye).normalize();
    root.traverseVisible((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const pos = mesh.geometry.attributes.position;
      // Every vertex for small meshes, a stride for large ones.
      const step = Math.max(1, Math.floor(pos.count / 20000));
      for (let k = 0; k < pos.count; k += step) {
        v.fromBufferAttribute(pos, k).applyMatrix4(mesh.matrixWorld);
        const d = v.sub(eye).dot(fwd);
        if (d > 1e-3) {
          lo = Math.min(lo, d);
          hi = Math.max(hi, d);
        }
      }
    });
  }
  root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
  material.dispose();
  if (!Number.isFinite(lo)) return null;
  const pad = Math.max(0.02 * (hi - lo), 1e-3);
  // The farthest content should not be exactly "far" (0 = background); leave a margin.
  return { near: Math.max(1e-3, lo - pad), far: hi + Math.max(pad, 0.15 * (hi - lo)) };
}
